import { MutableRefObject, useEffect, useState, useCallback } from 'react';

import { ComboboxSuggestions, SuggestionWithIdAndValue } from '../ComboboxTypes';
import { createSuggestions, findAndSetSelctedSuggestion, scrollIntoView } from '../utils';
import filterInSuggestions from './filterInSuggestions';

const useComboboxSelect = (
    suggestions: ComboboxSuggestions[],
    value: string,
    onChange: (value: string) => void,
    inputRef: MutableRefObject<HTMLInputElement | null>,
    wrapperRef: MutableRefObject<HTMLDivElement | null>,
    resetOnEmptyResult: boolean,
    listRef: HTMLUListElement | null,
    exactMatchOnChange: boolean,
    onSelect?: (value: string) => void,
) => {
    const [suggestionListOpen, setSuggestionListOpen] = useState(false);
    const [navSuggestion, setNavSuggestion] = useState<SuggestionWithIdAndValue | null>(null);
    const [currentSuggestions, setCurrentSuggestions] = useState<SuggestionWithIdAndValue[]>([]);

    const navigationOrInputValue = navSuggestion?.value || value;
    useEffect(() => {
        const newCurrentSuggestions = exactMatchOnChange
            ? filterInSuggestions(createSuggestions(suggestions, value), value)
            : createSuggestions(suggestions, value);
        setCurrentSuggestions(newCurrentSuggestions);
    }, [exactMatchOnChange, suggestions, value]);

    useEffect(() => {
        if (
            currentSuggestions.length &&
            currentSuggestions.length === 1 &&
            !currentSuggestions.some((suggestion) => suggestion.value === value) &&
            document?.activeElement === inputRef.current
        ) {
            setSuggestionListOpen(true);
        }
    }, [currentSuggestions, value, setSuggestionListOpen, inputRef]);

    const onFocus = useCallback(() => {
        setCurrentSuggestions(createSuggestions(suggestions, value));
    }, [suggestions, value, setCurrentSuggestions]);

    const handleSelect = useCallback(
        (suggestion: SuggestionWithIdAndValue) => {
            onSelect?.(suggestion.value);
            setSuggestionListOpen(false);
            setNavSuggestion(null);
            setCurrentSuggestions([]);
        },
        [onSelect, setSuggestionListOpen, setNavSuggestion, setCurrentSuggestions],
    );

    const onKeyDown = useCallback(
        (e: KeyboardEvent) => {
            const isManipulatingText = e.shiftKey || e.ctrlKey || e.altKey;
            const isNavigating =
                ['ArrowDown', 'ArrowUp', 'PageUp', 'PageDown', 'Home', 'End'].includes(e.key) && !isManipulatingText;

            const keysToIgnore = ['ArrowDown', 'ArrowLeft', 'ArrowUp', 'ArrowRight'];

            if (isNavigating && !suggestionListOpen) {
                setSuggestionListOpen(true);
                return;
            }
            if (isNavigating && suggestionListOpen) {
                findAndSetSelctedSuggestion(e, {
                    setNavSuggestion,
                    navSuggestion,
                    currentSuggestions,
                });
            }

            switch (e.key) {
                case 'Enter':
                    if (navSuggestion || (currentSuggestions.length > 0 && value.length > 0)) {
                        e.preventDefault();
                        handleSelect(navSuggestion ?? currentSuggestions[0]);
                    }
                    if (resetOnEmptyResult && !navSuggestion) {
                        onChange('');
                        onSelect?.('');
                        setNavSuggestion(null);
                    }
                    setSuggestionListOpen(false);
                    break;
                case 'Tab':
                case 'Delete':
                    setSuggestionListOpen(false);
                    break;
                case 'Escape':
                    if (suggestionListOpen) {
                        setSuggestionListOpen(false);
                    } else {
                        onChange('');
                    }
                    setNavSuggestion(null);
                    break;
                case 'Backspace':
                    onChange(navigationOrInputValue);
                    setNavSuggestion(null);
                    setSuggestionListOpen(true);
                    break;
                default:
                    if (keysToIgnore.includes(e.key)) {
                        break;
                    }

                    setSuggestionListOpen(true);
                    if (navSuggestion) {
                        onChange(navSuggestion.value);
                        setNavSuggestion(null);
                    } else {
                        onChange(value);
                    }
                    break;
            }
        },
        [
            onChange,
            setSuggestionListOpen,
            handleSelect,
            onSelect,
            setNavSuggestion,
            currentSuggestions,
            navSuggestion,
            navigationOrInputValue,
            resetOnEmptyResult,
            suggestionListOpen,
            value,
        ],
    );

    useEffect(() => {
        const input = inputRef.current;
        if (input) {
            input.addEventListener('keydown', onKeyDown);
        }
        return () => input?.removeEventListener('keydown', onKeyDown);
    }, [inputRef, onKeyDown]);

    useEffect(() => {
        if (suggestionListOpen && navSuggestion && listRef) {
            scrollIntoView(listRef, navSuggestion.id);
        }
    }, [navSuggestion, listRef, suggestionListOpen]);

    return {
        handleSelect,
        inputRef,
        suggestionListOpen,
        setSuggestionListOpen,
        navSuggestion,
        setNavSuggestion,
        currentSuggestions,
        navigationOrInputValue,
        onFocus,
    };
};

export default useComboboxSelect;
